import { faClock, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Time } from "@internationalized/date";
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TimeField as AriaTimeField, DateInput, DateSegment } from "react-aria-components";
import { tv } from "tailwind-variants";

import { cn } from "../../utils/cn";
import { Button as RivetButton } from "../button/Button";
import { Popover } from "../popover/Popover";

import type { CSSProperties, KeyboardEvent, ReactNode } from "react";
import type { TimeFieldProps as AriaTimeFieldProps, TimeValue } from "react-aria-components";
import type { VariantProps } from "tailwind-variants";
import type { SlotsToClasses } from "../../utils/types";

const timePickerVariants = tv({
  slots: {
    base: "group relative flex flex-col gap-1",
    label: "text-sm font-semibold text-gray-900 dark:text-gray-100",
    mainWrapper: "flex w-full flex-col",
    trigger: [
      "relative",
      "w-full",
      "inline-flex",
      "flex-row",
      "items-center",
      "gap-3",
      "px-3",
      "cursor-pointer",
      "shadow-sm",
      "outline-none",
      "tap-highlight-transparent",
      "border",
      "border-solid",
      "border-gray-150",
      "dark:border-gray-700",
      "bg-white",
      "dark:bg-gray-800",
      "rounded",
      "transition-colors",
      // Hover state
      "data-[hover=true]:border-gray-300",
      "dark:data-[hover=true]:border-gray-600",
      // Focus/open state
      "data-[open=true]:border-blue-400",
      "data-[focus-visible=true]:border-blue-400",
      "dark:data-[open=true]:border-blue-700",
      "dark:data-[focus-visible=true]:border-blue-700",
    ],
    triggerText: [
      "flex-1",
      "text-left",
      "font-normal",
      "text-gray-500",
      "dark:text-gray-300",
      "group-data-[has-value=true]:text-gray-900",
      "dark:group-data-[has-value=true]:text-white",
    ],
    triggerIcon: "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300",
    clearButton: [
      "flex",
      "items-center",
      "justify-center",
      "text-gray-400",
      "hover:text-gray-600",
      "dark:text-gray-500",
      "dark:hover:text-gray-300",
      "transition-colors",
    ],
    popover: [
      "rounded-md border border-gray-200 bg-white p-3 shadow-xl",
      "dark:border-gray-600 dark:bg-gray-850",
    ],
    timeField: "flex flex-col gap-2",
    timeInput: [
      "flex min-w-32 rounded border border-solid border-gray-150 px-3 py-1",
      "bg-white dark:bg-gray-800",
      "dark:border-gray-700",
      "focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100",
      "dark:focus-within:border-blue-700 dark:focus-within:ring-blue-900/30",
    ],
    timeSegment: [
      "outline-hidden caret-auto px-0.5 tabular-nums",
      "text-gray-900 dark:text-gray-100",
      "data-[placeholder]:text-gray-400 dark:data-[placeholder]:text-gray-500",
      "focus:rounded focus:bg-blue-100 dark:focus:bg-blue-700",
    ],
    nowButton: [
      "rounded",
      "border-gray-200 dark:border-gray-600",
      "dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600",
    ],
    popoverLabel: "text-sm font-medium text-gray-700 dark:text-gray-300",
    helperWrapper: "relative hidden flex-col gap-1.5 p-1 pt-1.5 group-data-[has-helper=true]:flex",
    description: "text-xs text-gray-400",
    errorMessage: "text-xs text-red",
  },
  variants: {
    size: {
      sm: {
        trigger: "h-7 min-h-7 px-2",
        triggerText: "text-sm",
        triggerIcon: "text-xs",
        label: "text-xs",
      },
      md: {
        trigger: "h-8 min-h-8",
        triggerText: "text-sm",
        triggerIcon: "text-sm",
      },
      lg: {
        trigger: "h-10 min-h-10 rounded-md",
        triggerText: "text-base",
        triggerIcon: "text-sm",
      },
    },
    isDisabled: {
      true: {
        base: "pointer-events-none opacity-60",
        trigger: "pointer-events-none cursor-not-allowed",
      },
    },
    isInvalid: {
      true: {
        label: "!text-red",
        trigger: "!border-red data-[open=true]:border-red",
        triggerText: "!text-red",
        triggerIcon: "text-red",
      },
    },
    isRequired: {
      true: {
        label: "after:ml-0.5 after:text-red after:content-['*']",
      },
    },
    fullWidth: {
      true: {
        base: "w-full",
      },
      false: {
        mainWrapper: "min-w-64",
      },
    },
    labelPlacement: {
      top: {
        base: "flex flex-col",
        label: "max-w-full overflow-hidden text-ellipsis",
      },
      left: {
        base: "flex-row flex-nowrap items-center data-[has-helper=true]:items-start",
        mainWrapper: "flex-1",
        label: "pr-2",
      },
    },
    hasOnlyErrorMessage: {
      true: {
        // Don't want error message to take up extra space since it will appear and disappear,
        // causing the layout to shift.
        helperWrapper: "absolute bottom-0 translate-y-full",
      },
    },
  },
  compoundVariants: [
    // left & size
    {
      labelPlacement: "left",
      size: "sm",
      class: {
        label: "group-data-[has-helper=true]:pt-2",
      },
    },
    {
      labelPlacement: "left",
      size: "md",
      class: {
        label: "group-data-[has-helper=true]:pt-3",
      },
    },
    {
      labelPlacement: "left",
      size: "lg",
      class: {
        label: "group-data-[has-helper=true]:pt-4",
      },
    },
  ],
  defaultVariants: {
    size: "md",
    isDisabled: false,
    isInvalid: false,
    isRequired: false,
    fullWidth: false,
    labelPlacement: "top",
    hasOnlyErrorMessage: false,
  },
});

type TimePickerSlots = keyof ReturnType<typeof timePickerVariants>;
type TimePickerVariantProps = VariantProps<typeof timePickerVariants>;

export interface TimePickerProps
  extends Omit<AriaTimeFieldProps<TimeValue>, "value" | "onChange" | "className">,
    Omit<TimePickerVariantProps, "hasOnlyErrorMessage"> {
  /** Optional class name applied to the root of the TimePicker. */
  className?: string;
  /** Controlled value. Pass `null` to clear the selection. */
  value?: TimeValue | null;
  /** Called when the value changes. Receives a TimeValue or null when cleared.
   *  Also provides the formatted time string as the second argument for antd compatibility.
   */
  onChange?: (value: TimeValue | null, timeString: string) => void;
  /** Called when the user confirms/adds the selected time (e.g., by clicking "Add" button) */
  onConfirm?: (value: TimeValue | null, timeString: string) => void;
  /** Placeholder text when no value is selected. */
  placeholder?: string;
  /** Time format string (e.g., "hh:mm a" for 12-hour, "HH:mm" for 24-hour).
   *  @default "hh:mm a"
   */
  format?: string;
  /** Whether to use 12-hour format.
   *  @default true
   */
  hourCycle?: 12 | 24;
  /** Whether to show the clear button when a value is selected.
   *  @default false
   */
  allowClear?: boolean;
  /** Whether the time picker modal is open */
  isOpen?: boolean;
  /** Handler for when the time picker modal is opened or closed */
  onOpenChange?: (isOpen: boolean) => void;
  /** Inline styles */
  style?: CSSProperties;
  /** The container element in which the popover portal will be placed. */
  portalContainer?: Element;
  /** Label text */
  label?: ReactNode;
  /** Description text shown below the input */
  description?: ReactNode;
  /** Error message shown when invalid */
  errorMessage?: ReactNode;
  /** Custom class names for different slots */
  classNames?: SlotsToClasses<TimePickerSlots>;
}

function formatTimeValue(value: TimeValue | null, format: string, hourCycle: 12 | 24): string {
  if (!value) return "";

  const hour = value.hour;
  const minute = value.minute;
  const second = value.second ?? 0;

  if (hourCycle === 12) {
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

    if (format.includes("ss")) {
      return `${displayHour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}:${second.toString().padStart(2, "0")} ${period}`;
    }
    return `${displayHour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")} ${period}`;
  }

  if (format.includes("ss")) {
    return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}:${second.toString().padStart(2, "0")}`;
  }
  return `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
}

export const TimePicker = forwardRef<HTMLDivElement, TimePickerProps>(
  (
    {
      className,
      classNames,
      value,
      onChange,
      onConfirm,
      placeholder = "Select time",
      format = "hh:mm a",
      hourCycle = 12,
      allowClear = false,
      isOpen,
      onOpenChange,
      style,
      portalContainer,
      label,
      description,
      errorMessage,
      size = "md",
      isDisabled = false,
      isInvalid: isInvalidProp = false,
      isRequired = false,
      fullWidth = false,
      labelPlacement = "top",
      ...props
    },
    ref
  ) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const isPopoverOpen = isOpen ?? internalIsOpen;
    const setIsPopoverOpen = onOpenChange ?? setInternalIsOpen;

    const hasValue = !!value;
    const hasHelper = !!description || !!errorMessage;
    const hasOnlyErrorMessage = !!errorMessage && !description;
    const isInvalid = isInvalidProp || !!errorMessage;

    const slots = useMemo(
      () =>
        timePickerVariants({
          size,
          isDisabled,
          isInvalid,
          isRequired,
          fullWidth,
          labelPlacement,
          hasOnlyErrorMessage,
        }),
      [size, isDisabled, isInvalid, isRequired, fullWidth, labelPlacement, hasOnlyErrorMessage]
    );

    const formattedTime = useMemo(
      () => (value ? formatTimeValue(value, format, hourCycle) : placeholder),
      [value, format, hourCycle, placeholder]
    );

    const handleClearPress = useCallback(() => {
      onChange?.(null, "");
    }, [onChange]);

    const handleTimeChange = useCallback(
      (newValue: TimeValue | null) => {
        const timeString = formatTimeValue(newValue, format, hourCycle);
        onChange?.(newValue, timeString);
      },
      [onChange, format, hourCycle]
    );

    const handleSetNow = useCallback(() => {
      const now = new Date();
      const timeValue = new Time(now.getHours(), now.getMinutes());
      handleTimeChange(timeValue);
    }, [handleTimeChange]);

    const handleConfirm = useCallback(() => {
      if (onConfirm && value) {
        const timeString = formatTimeValue(value, format, hourCycle);
        onConfirm(value, timeString);
        setIsPopoverOpen(false);
      }
    }, [onConfirm, value, format, hourCycle, setIsPopoverOpen]);

    // Auto-focus the first time segment when the popover opens
    const timeInputRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      if (isPopoverOpen) {
        // Use requestAnimationFrame to wait for the popover to render
        requestAnimationFrame(() => {
          const firstSegment = timeInputRef.current?.querySelector('[role="spinbutton"]');
          if (firstSegment instanceof HTMLElement) {
            firstSegment.focus();
          }
        });
      }
    }, [isPopoverOpen]);

    const handleTimeInputKeyDown = useCallback(
      (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter") {
          e.preventDefault();
          if (onConfirm && value) {
            handleConfirm();
          } else {
            setIsPopoverOpen(false);
          }
        }
      },
      [onConfirm, value, handleConfirm, setIsPopoverOpen]
    );

    // Determine if we should include seconds based on format
    const granularity = format.includes("ss") ? "second" : "minute";

    const labelContent = useMemo(
      () =>
        label ? <label className={cn(slots.label(), classNames?.label)}>{label}</label> : null,
      [label, slots, classNames?.label]
    );

    const helperWrapper = useMemo(() => {
      if (!hasHelper) return null;

      return (
        <div className={cn(slots.helperWrapper(), classNames?.helperWrapper)}>
          {errorMessage ? (
            <div className={cn(slots.errorMessage(), classNames?.errorMessage)}>{errorMessage}</div>
          ) : description ? (
            <div className={cn(slots.description(), classNames?.description)}>{description}</div>
          ) : null}
        </div>
      );
    }, [hasHelper, errorMessage, description, slots, classNames]);

    return (
      <div
        ref={ref}
        className={cn(slots.base(), classNames?.base, className)}
        style={style}
        data-slot="base"
        data-has-value={hasValue}
        data-has-helper={hasHelper}
        data-has-label={!!label}
        data-disabled={isDisabled}
        data-invalid={isInvalid}
      >
        {labelPlacement === "left" ? labelContent : null}
        <Popover
          placement="bottom-start"
          onOpenChange={setIsPopoverOpen}
          isOpen={isPopoverOpen}
          triggerScaleOnOpen={false}
          portalContainer={portalContainer}
        >
          <div className={cn(slots.mainWrapper(), classNames?.mainWrapper)}>
            {labelPlacement !== "left" ? labelContent : null}
            <Popover.Trigger>
              <RivetButton
                fullWidth
                disableAnimation
                aria-label={label ? undefined : "Open time picker"}
                className={cn(slots.trigger(), classNames?.trigger)}
                isDisabled={isDisabled}
                data-hover={isHovered}
                data-open={isPopoverOpen}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <span className={cn(slots.triggerText(), classNames?.triggerText)}>
                  {formattedTime}
                </span>
                {hasValue && allowClear ? (
                  <span
                    role="button"
                    tabIndex={0}
                    className={cn(slots.clearButton(), classNames?.clearButton)}
                    aria-label="Clear time"
                    onClick={e => {
                      e.stopPropagation();
                      handleClearPress();
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        handleClearPress();
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faXmark} className="size-3" />
                  </span>
                ) : (
                  <FontAwesomeIcon
                    icon={faClock}
                    className={cn(slots.triggerIcon(), classNames?.triggerIcon)}
                  />
                )}
              </RivetButton>
            </Popover.Trigger>
            {helperWrapper}
          </div>
          <Popover.Content className={cn(slots.popover(), classNames?.popover)}>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className={cn(slots.popoverLabel(), classNames?.popoverLabel)}>Time</span>
                <RivetButton
                  variant="default"
                  size="sm"
                  ghost
                  className={cn(slots.nowButton(), classNames?.nowButton)}
                  onPress={handleSetNow}
                >
                  Now
                </RivetButton>
              </div>
              {}
              <div onKeyDown={handleTimeInputKeyDown}>
                <AriaTimeField
                  value={value}
                  onChange={handleTimeChange}
                  hourCycle={hourCycle}
                  granularity={granularity}
                  className={cn(slots.timeField(), classNames?.timeField)}
                  isDisabled={isDisabled}
                  {...props}
                >
                  <DateInput
                    ref={timeInputRef}
                    className={cn(slots.timeInput(), classNames?.timeInput)}
                  >
                    {segment => (
                      <DateSegment
                        segment={segment}
                        className={cn(slots.timeSegment(), classNames?.timeSegment)}
                      >
                        {({ isPlaceholder, type, text }) => {
                          if (isPlaceholder) {
                            if (type === "hour") return "HH";
                            if (type === "minute") return "MM";
                            if (type === "second") return "SS";
                            if (type === "dayPeriod") return "AM";
                          }
                          return text;
                        }}
                      </DateSegment>
                    )}
                  </DateInput>
                </AriaTimeField>
              </div>
              {onConfirm && (
                <RivetButton
                  variant="primary"
                  size="md"
                  className="w-full"
                  onPress={handleConfirm}
                  isDisabled={!value}
                >
                  Add
                </RivetButton>
              )}
            </div>
          </Popover.Content>
        </Popover>
      </div>
    );
  }
);
TimePicker.displayName = "TimePicker";
